// src/components/Banners/EditBannerModal.js
import React, { useState, Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PhotoIcon } from "@heroicons/react/20/solid";
import { useDispatch, useSelector } from "react-redux";
import { updateBanner } from "../../redux/slices/bannerSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import placeholderImage from "../../assets/images/placeholder.jpg";
import { useTranslation } from "../../contexts/LanguageContext";

function EditBannerModal({ isOpen, setIsOpen, banner }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { banners = [] } = useSelector((state) => state.banners || {});
  const [formData, setFormData] = useState({
    image: null,
    imageBase64: null,
    sequenceNo: "",
    isActive: true,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [sequenceError, setSequenceError] = useState("");

  // Helper function to convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  useEffect(() => {
    if (banner) {
      setFormData({
        image: null,
        imageBase64: null,
        sequenceNo: banner.sequenceNo?.toString() || "",
        isActive: banner.isActive ?? true,
      });
      setImagePreview(banner.image || placeholderImage);
      setSequenceError(""); // Clear any previous errors
    }
  }, [banner]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "sequenceNo") {
      // Clear previous error
      setSequenceError("");
      
      // Check if sequence number already exists (excluding current banner)
      const sequenceExists = banners.some(
        b => b._id !== banner?._id && b.sequenceNo === Number(value)
      );
      if (sequenceExists && value) {
        setSequenceError(t("bannerForm.sequenceInUse").replace("{n}", value));
      }
    }
    
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === "checkbox" ? checked : value 
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const base64String = await convertToBase64(file);
        setFormData((prev) => ({ 
          ...prev, 
          image: file,
          imageBase64: base64String
        }));
        setImagePreview(URL.createObjectURL(file));
      } catch (error) {
        dispatch(showNotification({ message: t("bannerForm.errorProcessing"), type: "error" }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!banner?._id) {
      dispatch(showNotification({ message: t("bannerForm.idMissing"), type: "error" }));
      return;
    }

    // Check for duplicate sequence number before submitting (excluding current banner)
    const sequenceExists = banners.some(
      b => b._id !== banner._id && b.sequenceNo === Number(formData.sequenceNo)
    );
    if (sequenceExists) {
      setSequenceError(t("bannerForm.sequenceInUse").replace("{n}", formData.sequenceNo));
      return;
    }

    // Backend (PATCH /api/v2/banners/admins/:id) expects JSON
    // { image, isActive, sequenceNo }. Only include `image` when the user
    // picked a new one, so the existing image is preserved otherwise.
    const dataToSend = {
      sequenceNo: Number(formData.sequenceNo),
      isActive: formData.isActive,
    };
    if (formData.imageBase64) {
      dataToSend.image = formData.imageBase64;
    }

    dispatch(updateBanner({ id: banner._id, bannerData: dataToSend }))
      .unwrap()
      .then(() => {
        dispatch(showNotification({ message: t("bannerForm.updatedSuccess"), type: "success" }));
        setIsOpen(false);
      })
      .catch((error) => {
        const errorMessage = typeof error === "string" ? error : error?.message || t("bannerForm.failedUpdate");
        dispatch(showNotification({ message: errorMessage, type: "error" }));
      });
  };

  // Get available sequence numbers for suggestion (excluding current banner)
  const getAvailableSequenceNumbers = () => {
    const usedSequences = banners
      .filter(b => b._id !== banner?._id)
      .map(b => b.sequenceNo)
      .filter(seq => seq != null);
    const maxSequence = Math.max(0, ...usedSequences, banner?.sequenceNo || 0);
    const suggestions = [];
    
    // Include current sequence number as available
    if (banner?.sequenceNo && !usedSequences.includes(banner.sequenceNo)) {
      suggestions.push(banner.sequenceNo);
    }
    
    // Suggest next available numbers
    for (let i = 1; i <= maxSequence + 3; i++) {
      if (!usedSequences.includes(i) && !suggestions.includes(i)) {
        suggestions.push(i);
        if (suggestions.length >= 3) break;
      }
    }
    
    return suggestions.sort((a, b) => a - b);
  };

  const availableSequences = getAvailableSequenceNumbers();

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" open={isOpen} onClose={() => setIsOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="max-w-md w-full space-y-4 bg-white rounded-lg shadow-md p-6">
              <Dialog.Title className="text-lg font-medium">{t("bannerForm.editTitle")}</Dialog.Title>
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  {/* Banner Image Field */}
                  <div>
                    <label htmlFor="image" className="block text-sm font-medium text-gray-900">
                      {t("bannerForm.imageOptional")}
                    </label>
                    <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10 relative">
                      <input
                        id="file-upload"
                        name="image"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                      <div className="text-center">
                        <img 
                          src={imagePreview} 
                          alt="Current banner image" 
                          className="mx-auto h-24 w-32 object-cover rounded-md mb-2"
                          onError={(e) => { e.target.src = placeholderImage; }}
                        />
                        <PhotoIcon aria-hidden="true" className="mx-auto h-8 w-8 text-gray-300" />
                        <div className="mt-2 text-sm leading-6 text-gray-600">{t("bannerForm.clickToChange")}</div>
                        <p className="text-xs leading-5 text-gray-600">{t("bannerForm.fileHelp")}</p>
                      </div>
                    </div>
                  </div>

                  {/* Sequence Number Field */}
                  <div>
                    <label htmlFor="sequenceNo" className="block text-sm font-medium text-gray-900">
                      {t("bannerForm.sequenceNumberRequired")}
                    </label>
                    <input
                      id="sequenceNo"
                      name="sequenceNo"
                      type="number"
                      min="1"
                      value={formData.sequenceNo}
                      onChange={handleInputChange}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm ${
                        sequenceError
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-themeOrange'
                      }`}
                      placeholder={t("bannerForm.sequencePlaceholder")}
                      required
                    />
                    {sequenceError && (
                      <p className="mt-1 text-xs text-red-600">{sequenceError}</p>
                    )}
                    <div className="mt-1">
                      <p className="text-xs text-gray-500">{t("bannerForm.sequenceHelp")}</p>
                      {availableSequences.length > 0 && (
                        <p className="text-xs text-green-600">
                          {t("bannerForm.available")} {availableSequences.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Active Status Field */}
                  <div className="flex items-center">
                    <input
                      id="isActive"
                      name="isActive"
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-themeOrange focus:ring-themeOrange border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                      {t("bannerForm.setActive")}
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-6">
                  <button
                    type="button"
                    className="text-sm font-medium text-gray-700"
                    onClick={() => setIsOpen(false)}
                  >
                    {t("bannerForm.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={!!sequenceError}
                    className={`text-sm font-medium text-white rounded-md px-4 py-1 ${
                      sequenceError
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-themeOrange hover:bg-orange-600'
                    }`}
                  >
                    {t("bannerForm.saveChanges")}
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default EditBannerModal;